<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { fetchBoardGame, createBoardGame, updateBoardGame } from "./api";

const route = useRoute(), router = useRouter();
const id = computed(() => route.params.id || null);
const isEdit = computed(() => !!id.value);
const loading = ref(false), notFound = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref("");
const form = ref({
  gameName: "",
  alt: "",
  src: "",
  backgroundFill: "#164972",
  url: "",
  type: "score",
  order: 0,
  startDate: "",
  endDate: "",
});
const typeItems = [
  { title: "Points", value: "score" },
  { title: "Time", value: "time" },
];

onMounted(async () => {
  if (!isEdit.value) return;
  loading.value = true;
  try {
    const g = await fetchBoardGame(id.value);
    if (!g) { notFound.value = true; return; }
    form.value = {
      gameName: g.gameName ?? "",
      alt: g.alt ?? "",
      src: g.src ?? "",
      backgroundFill: g.backgroundFill ?? "#164972",
      url: g.url ?? "",
      type: g.type === "time" ? "time" : "score",
      order: g.order ?? 0,
      startDate: g.startDate ?? "",
      endDate: g.endDate ?? "",
    };
  } catch { error.value = "Couldn't load this game."; }
  finally { loading.value = false; }
});

async function save() {
  success.value = false; error.value = ""; validationError.value = "";
  const f = form.value;
  if (!isEdit.value && !f.gameName.trim()) { validationError.value = "Game Name (id) is required."; return; }
  if (!f.alt.trim()) { validationError.value = "Display name is required."; return; }
  if (!f.src.trim()) { validationError.value = "Image filename is required."; return; }
  if (!f.url.trim()) { validationError.value = "Play URL is required."; return; }
  if (f.startDate && f.endDate && f.startDate > f.endDate) { validationError.value = "Start date must be before end date."; return; }
  saving.value = true;
  try {
    const payload = {
      alt: f.alt,
      src: f.src,
      backgroundFill: f.backgroundFill,
      url: f.url,
      type: f.type,
      order: Number(f.order) || 0,
      startDate: f.startDate,
      endDate: f.endDate,
    };
    if (isEdit.value) await updateBoardGame(id.value, payload);
    else await createBoardGame({ gameName: f.gameName.trim(), ...payload });
    success.value = true;
    setTimeout(() => router.push({ name: "board-games" }), 800);
  } catch (err) {
    error.value = err?.response?.data?.message || "Error occurred. Please try again.";
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'board-games' })">Back</VBtn>
    <h1 class="text-h5 mb-4">{{ isEdit ? "Edit Board Game" : "Add Board Game" }}</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Game not found.</VAlert>
    <VCard v-else class="pa-4" max-width="640">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved. Redirecting…</VAlert>
      <form @submit.prevent="save">
        <VRow>
          <VCol cols="12" md="6">
            <VTextField
              v-model="form.gameName"
              label="Game Name (id)"
              :disabled="isEdit"
              hint="Unique id, e.g. othello26. Can't be changed after creation."
              persistent-hint
            />
          </VCol>
          <VCol cols="12" md="6">
            <VTextField v-model="form.alt" label="Display Name" placeholder="Othello" />
          </VCol>
          <VCol cols="12" md="6">
            <VTextField
              v-model="form.src"
              label="Image Filename"
              placeholder="Othello Game.jpg"
              hint="File in src/assets/wellness2023/board-game/"
              persistent-hint
            />
          </VCol>
          <VCol cols="12" md="6">
            <VTextField v-model="form.backgroundFill" label="Accent Color" placeholder="#164972">
              <template #append-inner>
                <div
                  :style="{ background: form.backgroundFill, width: '20px', height: '20px', borderRadius: '50%', border: '1px solid #ccc' }"
                />
              </template>
            </VTextField>
          </VCol>
          <VCol cols="12">
            <VTextField v-model="form.url" label="Play URL" placeholder="https://aspials.com/games/othello?id=" />
          </VCol>
          <VCol cols="12" md="4">
            <VSelect v-model="form.type" :items="typeItems" item-title="title" item-value="value" label="Score Type" />
          </VCol>
          <VCol cols="12" md="4">
            <VTextField v-model.number="form.order" type="number" label="Display Order" />
          </VCol>
          <VCol cols="12" md="4" />
          <VCol cols="12" md="6">
            <VTextField v-model="form.startDate" type="date" label="Start Date" />
          </VCol>
          <VCol cols="12" md="6">
            <VTextField v-model="form.endDate" type="date" label="End Date" />
          </VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mb-4">{{ validationError }}</VAlert>
        <VBtn type="submit" :loading="saving" class="mt-2">Save</VBtn>
      </form>
    </VCard>
  </section>
</template>
